import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/layout/SiteHeader/SiteHeader';
import SiteFooter from '@/components/layout/SiteFooter/SiteFooter';
import Section from '@/components/ui/Section/Section';
import SectionHeader from '@/components/ui/SectionHeader/SectionHeader';
import ShirabeSection from '@/components/landing/ShirabeSection/ShirabeSection';
import { usePageSeo } from '@/hooks/usePageSeo';
import { useScrolled } from '@/hooks/useScrolled';
import { CONTACT_URL, PARTNER_SAMPLE } from '@/lib/brand';
import styles from './Landing.module.css';

// C 向け LP。構成と見た目は姉妹サービス「まどりの構造チェック」の LP に揃えている:
// ヒーロー → 活用シーン → 使い方 → わかること → 判定の見方 → データ → FAQ → 注意 → 最終 CTA。
// 紙色と白を交互に敷き、カードは白＋1px 罫線 (白い帯の上では紙色に反転)。

const BADGES = ['無料', '登録不要', '数秒で表示', '国の公開データ'];

const SCENES = [
  {
    title: '気になる土地・建売を見つけた',
    body: '見学や問い合わせの前に、住所を入れてひととおり確認。災害や地盤で気になる点があれば、現地や不動産会社で確かめたいことがはっきりします。',
  },
  {
    title: 'いくつかの候補地で迷っている',
    body: '調べた土地は候補地として残り、並べて比べられます。価格や見た目だけでなく、災害・地盤・暮らしの面からも比べられます。',
  },
  {
    title: '工務店・不動産会社との打合せの前に',
    body: '「この浸水想定はどう考えればいいですか」「地盤調査はどうなりますか」。聞きたいことを、データを見ながら整理しておけます。',
  },
];

const STEPS = [
  {
    title: '住所を入力する',
    body: '調べたい土地の住所を入れます。番地まで指定するか、地図をクリックして場所を選ぶこともできます。',
  },
  {
    title: 'データが自動で集まる',
    body: '国が公開している災害・地盤・都市計画・価格・人口などのデータを自動で集めて、八つの章のレポートに整えます。数秒ほどで表示されます。',
  },
  {
    title: '立地レポートを読む',
    body: '最初に ◯・△ の結論、そのあとに章ごとのくわしい内容。地図と出典つきで、章を行き来しながら何度でも読み返せます。',
  },
];

const MARKS = [
  {
    kind: 'ok',
    sign: '◯',
    title: '該当なし',
    body: '公開データの上では、気をつけたい指定や想定に当てはまっていません。',
  },
  {
    kind: 'warn',
    sign: '△',
    title: '要確認',
    body: '当てはまる項目があります。どの程度のものか、章の中身と出典で確かめておきたい状態です。',
  },
  {
    kind: 'warn',
    sign: '△',
    title: '要注意',
    body: '影響の大きい指定や想定に当てはまっています。契約の前に、自治体のハザードマップや専門家への確認をおすすめします。',
  },
] as const;

const SOURCES = [
  '国土交通省 不動産情報ライブラリ',
  '国土地理院 地形図・土地条件図',
  '各市区町村 ハザードマップ',
  '政府統計 e-Stat 国勢調査',
  '地価公示・都道府県地価調査',
  '都市計画決定GISデータ',
];

const FAQ = [
  {
    q: '無料ですか？登録は必要ですか？',
    a: ['無料です。会員登録もメールアドレスの入力も必要ありません。スマートフォンでもパソコンでも使えます。'],
  },
  {
    q: '何がわかりますか？',
    a: [
      '土地のあらまし、災害リスク、地盤と造成、建てられる家のめやす、価格相場、暮らしの環境、これからの見通し、まとめと注意ごと。この八つの章で、ひとつの土地をいろいろな角度から確認できます。',
    ],
  },
  {
    q: '◯・△ は何を意味しますか？',
    a: [
      '災害・地盤・建築・暮らしの四つの面について、公開データの上で気をつけたい項目に当てはまるかどうかを示します。◯ は「該当なし」、△ は「要確認」または「要注意」です。',
      '点数や順位は出しません。◯ は安全を保証するものではなく、△ は「買ってはいけない」という意味でもありません。',
    ],
  },
  {
    q: 'どんなデータを使っていますか？',
    a: [
      '国土交通省の不動産情報ライブラリ、国土地理院、政府統計 e-Stat など、公開されている一次データです。事実は章ごとに出典つきで記載しているので、ご自身で原典に当たれます。',
    ],
  },
  {
    q: '入力した住所はどう扱われますか？',
    a: [
      '公開データを照会するために、住所（地図上の位置）をサーバーへ送ります。氏名やメールアドレスなどの入力はありません。',
      '調べた候補地の一覧は、お使いのブラウザの中に保存され、同じ端末でまた開けます。',
    ],
  },
  {
    q: '重要事項説明やハザードマップの代わりになりますか？',
    a: [
      'なりません。立地レポートは、土地のことを知るための参考資料です。契約の前には、宅地建物取引士による重要事項説明と、各自治体の最新のハザードマップを必ず確認してください。',
    ],
  },
  {
    q: '価格は、その土地の値段ですか？',
    a: [
      'いいえ。周辺の取引価格や地価公示にもとづく相場のめやすです。個別の土地の価格は、形や接道、時期によって変わります。',
    ],
  },
  {
    q: '工務店・設計事務所でも使えますか？',
    a: [
      'お使いいただけます。施主様名と貴社名を入れた PDF を作れる、貴社ブランド版（パートナー版）もご用意しています。お問い合わせからご連絡ください。',
    ],
  },
];

const SHARE_TEXT = '住所を入れるだけで、土地の災害リスク・地盤・価格相場を無料でチェック';

function shareUrl(): string {
  return new URL(import.meta.env.BASE_URL, window.location.origin).href;
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
  );
}

function HeroFigure() {
  return (
    <figure className={styles.heroFig}>
      <svg
        viewBox="0 0 520 330"
        role="img"
        aria-label="住所を入れると、災害リスク・地盤・暮らしの環境などが ◯ △ で表示される流れの図"
      >
        <defs>
          <pattern id="lp-grid" width="26" height="26" patternUnits="userSpaceOnUse">
            <path d="M26 0H0V26" fill="none" stroke="rgba(249,236,204,.22)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect x="8" y="8" width="300" height="314" rx="8" fill="rgba(255,255,255,.06)" stroke="rgba(249,236,204,.35)" />
        <rect x="22" y="22" width="272" height="286" fill="url(#lp-grid)" />
        {/* 川 */}
        <path d="M22 236c50-18 82 14 132-4s86-40 140-30" stroke="#7fb2ea" strokeWidth="14" fill="none" strokeLinecap="round" opacity=".8" />
        {/* 道 */}
        <g stroke="rgba(249,236,204,.5)" strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M22 96h272M100 22v170M214 22v150M22 160h130" />
        </g>
        {/* 街区 */}
        <g fill="rgba(249,236,204,.14)" stroke="rgba(249,236,204,.4)" strokeWidth="1.5">
          <rect x="36" y="36" width="50" height="46" />
          <rect x="114" y="36" width="86" height="46" />
          <rect x="228" y="36" width="52" height="46" />
          <rect x="36" y="110" width="50" height="36" />
          <rect x="228" y="110" width="52" height="50" />
        </g>
        {/* 調べる土地 */}
        <rect x="114" y="110" width="86" height="58" fill="rgba(224,122,47,.22)" stroke="#e07a2f" strokeWidth="4" />
        {/* ピン */}
        <path d="M157 92c-13 0-22 10-22 21 0 16 22 34 22 34s22-18 22-34c0-11-9-21-22-21z" fill="#e07a2f" stroke="#f9eccc" strokeWidth="2.5" />
        <circle cx="157" cy="113" r="7" fill="#f9eccc" />
        {/* 住所欄 */}
        <rect x="36" y="262" width="244" height="34" rx="17" fill="rgba(255,255,255,.95)" />
        <text x="54" y="284" fontSize="13" fill="#1b4280" fontWeight="700" fontFamily="sans-serif">
          ◯◯市△△町 1-2-3
        </text>
        {/* 矢印 */}
        <path d="M320 165h26m0 0-8-8m8 8-8 8" stroke="#f9eccc" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {/* 結果カード */}
        <g fontFamily="sans-serif">
          <rect x="356" y="36" width="156" height="78" rx="8" fill="rgba(255,255,255,.95)" />
          <text x="372" y="66" fontSize="13" fill="#1b4280" fontWeight="700">災害リスク</text>
          <text x="372" y="86" fontSize="11" fill="#333">洪水・土砂・津波</text>
          <text x="478" y="80" fontSize="30" fill="#8a5300" fontWeight="700" textAnchor="middle">△</text>

          <rect x="356" y="126" width="156" height="78" rx="8" fill="rgba(255,255,255,.95)" />
          <text x="372" y="156" fontSize="13" fill="#1b4280" fontWeight="700">地盤</text>
          <text x="372" y="176" fontSize="11" fill="#333">地形・揺れやすさ</text>
          <text x="478" y="170" fontSize="30" fill="#2e7d5b" fontWeight="700" textAnchor="middle">◯</text>

          <rect x="356" y="216" width="156" height="78" rx="8" fill="rgba(255,255,255,.95)" />
          <text x="372" y="246" fontSize="13" fill="#1b4280" fontWeight="700">暮らしの環境</text>
          <text x="372" y="266" fontSize="11" fill="#333">駅・学校・病院</text>
          <text x="478" y="260" fontSize="30" fill="#2e7d5b" fontWeight="700" textAnchor="middle">◯</text>
        </g>
      </svg>
      <figcaption>住所を入れると、土地のことが ◯・△ と八つの章で返ります（図は模式図です）</figcaption>
    </figure>
  );
}

function ShareRow() {
  const [done, setDone] = useState('');
  const url = shareUrl();
  // スマホで共有シートが使えるときはそれを、使えなければクリップボードにコピーする。
  const canShare =
    typeof navigator.share === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const say = (msg: string) => {
    setDone(msg);
    window.setTimeout(() => setDone(''), 4000);
  };
  const failed = () => say('コピーできませんでした。LINE・X のボタンをご利用ください。');
  const copyOrShow = () => {
    if (!navigator.clipboard?.writeText) return failed();
    navigator.clipboard.writeText(url).then(() => say('リンクをコピーしました'), failed);
  };
  const copy = () => {
    if (!canShare) return copyOrShow();
    navigator.share({ title: document.title, url }).catch((err: unknown) => {
      // 利用者が共有を取り消しただけのときは何もしない。それ以外はコピーへ落とす。
      if (err instanceof DOMException && err.name === 'AbortError') return;
      copyOrShow();
    });
  };
  return (
    <div className={styles.shareRow}>
      <p className={styles.shareLabel}>土地をさがしているご家族・ご友人にも教えてあげてください。</p>
      <ul className={styles.shareBtns}>
        <li>
          <a
            href={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            LINEで送る
          </a>
        </li>
        <li>
          <a
            href={`https://x.com/intent/post?url=${encodeURIComponent(url)}&text=${encodeURIComponent(SHARE_TEXT)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Xで共有
          </a>
        </li>
        <li>
          <button type="button" onClick={copy}>
            {canShare ? '共有する' : 'リンクをコピー'}
          </button>
        </li>
      </ul>
      <p className={styles.shareDone} role="status">
        {done}
      </p>
    </div>
  );
}

export default function Landing() {
  // 固定 CTA はヒーローを過ぎてから出す。
  const pastHero = useScrolled(320);
  usePageSeo({ canonical: '/' });
  return (
    <>
      <SiteHeader variant="lp" overlay />
      <main id="top">
        {/* 1. ファーストビュー */}
        <section className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroWrap}>
            <div>
              <p className={styles.eyebrow}>家を建てる土地をさがしている方へ</p>
              <h1 className={styles.heroTitle} id="hero-title">
                土地を決める前に、
                <br />
                立地をたしかめる。
              </h1>
              <p className={styles.heroLead}>
                住所を入れるだけで、その土地の災害リスク・地盤・価格相場から、暮らしの環境と将来の見通しまで、まとめて確認できます。
              </p>
              <ul className={styles.badges}>
                {BADGES.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <Link to="/report" className={`${styles.btn} ${styles.btnOnNavy} ${styles.btnCut}`}>
                無料で調べてみる
                <ArrowIcon />
              </Link>
              <span className={styles.btnNote}>
                登録不要・数秒で表示。スマートフォンでもパソコンでも使えます。
              </span>
              <p className={styles.heroScope}>
                国が公開しているデータをもとにした参考資料です。重要事項説明や、自治体のハザードマップ、専門家の判断に代わるものではありません。
              </p>
            </div>
            <HeroFigure />
          </div>
        </section>

        {/* 2. 活用シーン */}
        <Section id="scenes" aria-labelledby="scenes-title">
          <SectionHeader
            overline="活用シーン"
            title="こんなときに使えます"
            titleId="scenes-title"
            lead="契約の前のほうが、選び直せる余地が大きく残っています。"
          />
          <div className={`${styles.cards} ${styles.c3}`}>
            {SCENES.map((s) => (
              <div key={s.title} className={styles.card}>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 3. 使い方 */}
        <Section alt id="how" aria-labelledby="how-title">
          <SectionHeader
            overline="使い方"
            title="使い方は3ステップ"
            titleId="how-title"
            lead="特別な知識も、資料の準備も要りません。住所がわかれば始められます。"
          />
          <div className={styles.steps}>
            {STEPS.map((s) => (
              <div key={s.title} className={styles.step}>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <p className={styles.noteStrong}>
            <strong>会員登録もインストールも要りません。</strong>
            調べた土地は候補地としてお使いのブラウザに残るので、あとから見返したり、並べて比べたりできます。
          </p>
          <p className={styles.midCta}>
            <Link to="/report" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnCut}`}>
              まず住所を入れてみる
              <ArrowIcon />
            </Link>
          </p>
        </Section>

        {/* 4. 何がわかる？ */}
        <ShirabeSection />

        {/* 5. 判定の見方 */}
        <Section alt id="marks" aria-labelledby="marks-title">
          <SectionHeader
            overline="判定の見方"
            title="結論は ◯・△ で、最初に"
            titleId="marks-title"
            lead="災害・地盤・建築・暮らしの四つの面を、それぞれ ◯・△ で示します。点数や順位は出しません。"
          />
          <div className={styles.marks}>
            {MARKS.map((m) => (
              <div key={m.title} className={`${styles.mark} ${styles[m.kind]}`}>
                <span className={styles.sign} aria-hidden="true">
                  {m.sign}
                </span>
                <h3>{m.title}</h3>
                <p>{m.body}</p>
              </div>
            ))}
          </div>
          <p className={styles.noteStrong}>
            ◯・△ は、<strong>公開データに当てはまるかどうか</strong>
            を示すものです。◯ は安全を保証するものではなく、△ は「買ってはいけない」という意味でもありません。気になる項目は、章の中身と出典で確かめてください。
          </p>
        </Section>

        {/* 6. データのこと */}
        <Section id="data" aria-labelledby="data-title">
          <SectionHeader overline="データのこと" title="国が公開する一次データを、出典つきで" titleId="data-title" />
          <div className={styles.basis}>
            <div className={styles.panel}>
              <h3>どんなデータを使っているか</h3>
              <p>
                立地レポートは、すべて公開されている一次データに基づいています。事実は出典つきで記載し、ご自身で原典に当たれるようにしています。
              </p>
              <ul className={styles.sourceList} aria-label="主要な出典">
                {SOURCES.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
            <div className={styles.panel}>
              <h3>なぜ、土地を決める前に立地を見るのか</h3>
              <p>
                間取りや設備はあとから変えられますが、土地は選び直せません。災害、地形、水、道、人口。土地が長い時間のなかで残してきた痕跡は、これからそこで暮らしていけるかを知るための、確かな手がかりです。
              </p>
              <p>
                価格や見た目では分からないことを、契約の前にひととおり確かめておく。そのための道具です。
              </p>
            </div>
          </div>
        </Section>

        {/* 7. FAQ */}
        <Section alt id="faq" aria-labelledby="faq-title">
          <SectionHeader overline="FAQ" title="よくある質問" titleId="faq-title" />
          <div className={styles.faq}>
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <div className={styles.answer}>
                  {f.a.map((p) => (
                    <p key={p}>{p}</p>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </Section>

        {/* 8. 免責・注意 */}
        <Section aria-labelledby="disclaimer-title">
          <div className={styles.disclaimer}>
            <h2 id="disclaimer-title">ご利用にあたっての注意</h2>
            <ul>
              <li>
                立地レポートは、土地のことを知るための参考資料です。宅地建物取引業法に定める重要事項説明、建築確認申請、災害時の避難判断に代わるものではありません。
              </li>
              <li>
                内容は公開データにもとづくもので、現地の状況や最新の指定と異なる場合があります。最終的なご判断は、資格を持つ専門家への確認と、各自治体の最新情報をもって行ってください。
              </li>
              <li>価格は周辺の取引や地価公示にもとづく相場のめやすで、個別の土地の価格を示すものではありません。</li>
            </ul>
          </div>
        </Section>

        {/* 9. 最終 CTA */}
        <section className={styles.final} aria-labelledby="final-title">
          <div className={styles.finalWrap}>
            <h2 id="final-title">土地を決める前に、立地をたしかめる。</h2>
            <p>無料・登録不要・数秒で表示。気になる土地の住所をご用意ください。</p>
            <Link to="/report" className={`${styles.btn} ${styles.btnOnNavy}`}>
              無料で調べてみる
            </Link>
            <ShareRow />
            <p className={styles.biz}>
              工務店・設計者の方へ：施主様名と貴社名を入れた PDF を作れる、貴社ブランド版をご用意しています。実例として
              <a href={PARTNER_SAMPLE.url} target="_blank" rel="noopener noreferrer">
                {PARTNER_SAMPLE.name}
              </a>
              をご覧いただけます。ご相談は<a href={CONTACT_URL}>お問い合わせ</a>からどうぞ。
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
      <div className={styles.stickySpacer} aria-hidden="true" />

      <div className={`${styles.stickyCta} ${pastHero ? '' : styles.stickyHidden}`}>
        <Link to="/report" className={`${styles.btn} ${styles.btnPrimary}`}>
          無料で調べてみる（登録不要）
        </Link>
      </div>
    </>
  );
}
